// Localização: frontend/src/pages/Assinatura/CheckoutPage.jsx
// Página de checkout para assinatura usando Stripe

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Loader2,
    AlertCircle,
    CheckCircle2,
    Shield,
    Lock,
    ShieldCheck,
    CreditCard,
    Eye,
    FileLock,
    Server,
} from 'lucide-react';
import CheckoutForm from '../../components/Stripe/CheckoutForm';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';


const CheckoutPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [plano, setPlano] = useState(null);

    const planoId = searchParams.get('plano_id');

    useEffect(() => {
        const carregarPlano = async () => {
            if (!planoId) {
                setError('ID do plano não fornecido');
                setLoading(false);
                return;
            }

            try {
                const response = await api.get(`/assinaturas/planos/${planoId}`);
                if (response.data.success && response.data.data) {
                    setPlano(response.data.data);
                } else {
                    setError('Plano não encontrado');
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Erro ao carregar informações do plano');
            } finally {
                setLoading(false);
            }
        };

        carregarPlano();
    }, [planoId]);

    const handleSuccess = (data) => {
        // Se for upgrade, mostrar mensagem especial e redirecionar
        if (data.is_upgrade) {
            // Redirecionar para página de sucesso com flag de upgrade
            navigate(`/assinatura/sucesso?subscription_id=${data.subscription_id}&status=${data.status || 'active'}&upgrade=true`);
        } else {
            // Redirecionar para página de sucesso normal
            navigate(`/assinatura/sucesso?subscription_id=${data.subscription_id}&status=active`);
        }
    };

    const handleError = (error) => {
        // Redirecionar para página de falha
        navigate('/assinatura/falha', {
            state: {
                error: error.message || 'Erro ao processar pagamento'
            }
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
                    <p className="text-gray-600">Carregando informações do plano...</p>
                </div>
            </div>
        );
    }

    if (error && !plano) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Erro</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/pricing')}
                        className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                        Voltar para Planos
                    </button>
                </div>
            </div>
        );
    }

    if (!plano) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
            <div className="py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <button
                            onClick={() => navigate('/pricing')}
                            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors group"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                            <span>Voltar para Planos</span>
                        </button>
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Finalizar Assinatura</h1>
                        <p className="text-gray-600">
                            Complete o pagamento para ativar sua assinatura
                        </p>
                    </div>

                    {/* Erro */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-start">
                                <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Layout Dividido */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Coluna Esquerda - Resumo do Plano */}
                        <div className="space-y-6">
                            {/* Resumo do Plano */}
                            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl shadow-xl p-8 border border-emerald-100">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                                        <CheckCircle2 className="h-6 w-6 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900">Resumo da Assinatura</h2>
                                </div>
                                <div className="space-y-5">
                                    <div className="flex justify-between items-center py-4 border-b border-emerald-200">
                                        <span className="text-gray-600 font-medium text-lg">Plano</span>
                                        <span className="font-bold text-gray-900 text-xl">{plano.nome}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-4 border-b border-emerald-200">
                                        <span className="text-gray-600 font-medium text-lg">Valor</span>
                                        <div className="text-right">
                                            <span className="font-bold text-emerald-600 text-3xl">
                                                R$ {parseFloat(plano.valor).toFixed(2).replace('.', ',')}
                                            </span>
                                            <p className="text-sm text-gray-500 mt-1">
                                                por {plano.frequencia === 'monthly' ? 'mês' : 'ano'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center py-4">
                                        <span className="text-gray-600 font-medium text-lg">Frequência</span>
                                        <span className="font-semibold text-gray-900 text-lg">
                                            {plano.frequencia === 'monthly' ? 'Mensal' : 'Anual'}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Badge de Segurança */}
                                <div className="mt-6 pt-6 border-t border-emerald-200">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Shield className="h-5 w-5 text-emerald-600" />
                                        <span>Pagamento 100% seguro processado pelo Stripe</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Coluna Direita - Formulário de Pagamento */}
                        <div className="lg:sticky lg:top-8 lg:self-start">
                            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Dados de Pagamento</h2>
                                <CheckoutForm
                                    planName={plano.nome}
                                    planValue={parseFloat(plano.valor)}
                                    plano_id={plano.id}
                                    onSuccess={handleSuccess}
                                    onError={handleError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer de Segurança - Ocupa toda a largura */}
            <footer className="bg-gray-900 text-white w-full">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <h3 className="text-2xl font-bold mb-8 text-center">
                        Segurança e Proteção dos Dados
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Criptografia SSL */}
                        <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all hover:shadow-lg">
                            <div className="p-3 bg-emerald-600/20 rounded-lg flex-shrink-0">
                                <Lock className="h-6 w-6 text-emerald-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">Criptografia SSL</h4>
                                <p className="text-sm text-gray-300">
                                    Todas as informações são transmitidas com criptografia de ponta a ponta.
                                </p>
                            </div>
                        </div>

                        {/* Proteção de Dados */}
                        <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all hover:shadow-lg">
                            <div className="p-3 bg-blue-600/20 rounded-lg flex-shrink-0">
                                <ShieldCheck className="h-6 w-6 text-blue-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">Proteção de Dados</h4>
                                <p className="text-sm text-gray-300">
                                    Seus dados pessoais são protegidos conforme a LGPD.
                                </p>
                            </div>
                        </div>

                        {/* Pagamento Seguro */}
                        <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all hover:shadow-lg">
                            <div className="p-3 bg-purple-600/20 rounded-lg flex-shrink-0">
                                <CreditCard className="h-6 w-6 text-purple-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">Pagamento Seguro</h4>
                                <p className="text-sm text-gray-300">
                                    Processado pelo Stripe, líder mundial em pagamentos online.
                                </p>
                            </div>
                        </div>

                        {/* Privacidade */}
                        <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all hover:shadow-lg">
                            <div className="p-3 bg-indigo-600/20 rounded-lg flex-shrink-0">
                                <Eye className="h-6 w-6 text-indigo-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">Privacidade</h4>
                                <p className="text-sm text-gray-300">
                                    Nunca compartilhamos suas informações com terceiros.
                                </p>
                            </div>
                        </div>

                        {/* Dados Criptografados */}
                        <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all hover:shadow-lg">
                            <div className="p-3 bg-orange-600/20 rounded-lg flex-shrink-0">
                                <FileLock className="h-6 w-6 text-orange-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">Armazenamento Seguro</h4>
                                <p className="text-sm text-gray-300">
                                    Todos os dados são armazenados com criptografia avançada.
                                </p>
                            </div>
                        </div>

                        {/* Infraestrutura */}
                        <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all hover:shadow-lg">
                            <div className="p-3 bg-teal-600/20 rounded-lg flex-shrink-0">
                                <Server className="h-6 w-6 text-teal-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">Infraestrutura Robusta</h4>
                                <p className="text-sm text-gray-300">
                                    Servidores seguros com monitoramento 24/7.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Informação Adicional */}
                    <div className="mt-8 pt-8 border-t border-gray-700 text-center">
                        <p className="text-sm text-gray-300">
                            🔒 Seus dados são protegidos com os mais altos padrões de segurança da indústria.
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                            O pagamento é processado de forma segura pelo Stripe. Não armazenamos informações de cartão de crédito.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CheckoutPage;

